// src/ecrans/calendrier/calendrier-calculs.js — les chiffres et la place des
// barres du Calendrier NEST, depuis `ecran_calendrier_sejours_lire`,
// `ecran_logements_lire` et `ecran_fermetures_lire` (fonctions pures).
//
// Un séjour occupe les nuits de son arrivée (comprise) à son départ (exclu).
// Un séjour annulé n'occupe rien : il reste affiché, barré, si on le demande.

import { decalerIso, plateformeDe, pl } from "../nest";

const ANNULE = /cancel|annul/i;

export function estAnnule(s) {
  return Boolean(s && s.statut && ANNULE.test(String(s.statut)));
}

/** Un blocage sans voyageur (forme annoncée : type « ferme »). */
export function estFerme(s) {
  return Boolean(s && (s.type === "ferme" || s.type === "fermeture" || s.type === "blocage"));
}

/** Premier jour du mois et premier jour du mois suivant. */
export function bornesMois(moisIso) {
  const [a, m] = moisIso.split("-").map(Number);
  const du = `${a}-${String(m).padStart(2, "0")}-01`;
  const na = m === 12 ? a + 1 : a;
  const nm = m === 12 ? 1 : m + 1;
  return { du, au: `${na}-${String(nm).padStart(2, "0")}-01` };
}

export function moisDecale(moisIso, n) {
  const [a, m] = moisIso.split("-").map(Number);
  const t = a * 12 + (m - 1) + n;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, "0")}`;
}

/** Les jours [du, au) en « AAAA-MM-JJ ». */
export function joursEntre(du, au) {
  const out = [];
  for (let d = du; d < au; d = decalerIso(d, 1)) out.push(d);
  return out;
}

/** Nombre de nuits d'un séjour comprises dans [du, au). */
export function nuitsDans(s, du, au) {
  const debut = s.arrivee > du ? s.arrivee : du;
  const fin = s.depart < au ? s.depart : au;
  return fin > debut ? joursEntre(debut, fin).length : 0;
}

/**
 * Les quatre tuiles du mois. `logements` : ceux qui se louent ; `sejours` :
 * la réponse de la porte. Rend des nombres, ou null si une entrée manque.
 */
export function chiffresMois({ logements, sejours, du, au, exclus }) {
  if (!Array.isArray(logements) || !Array.isArray(sejours)) return null;
  // Les logements sans source VIVANTE de réservations (couverture de la
  // journée) sortent du remplissage et des nuits libres : leurs nuits
  // « libres » ne sont pas sues. Tout exclu → null (« — »), jamais 0 %.
  const sortis = exclus instanceof Set ? exclus : new Set(exclus || []);
  const parc = new Set(logements.map((l) => l.id).filter((id) => !sortis.has(id)));
  const nbJours = joursEntre(du, au).length;
  const total = parc.size * nbJours;
  let reservees = 0;
  let fermees = 0;
  let nbResas = 0;
  let nbFermes = 0;
  let nbAnnules = 0;
  for (const s of sejours) {
    if (!s.logement || !parc.has(s.logement.id)) continue;
    if (estAnnule(s)) {
      nbAnnules += 1;
      continue;
    }
    const n = nuitsDans(s, du, au);
    if (estFerme(s)) {
      fermees += n;
      nbFermes += 1;
    } else {
      reservees += n;
      nbResas += 1;
    }
  }
  return {
    rempli: total ? Math.round((reservees / total) * 100) : null,
    libres: total ? Math.max(0, total - reservees - fermees) : null,
    nbResas,
    nbFermes,
    nbAnnules,
  };
}

/** Les arrivées (non annulées, pas des blocages) entre deux jours [du, au). */
export function arriveesEntre(sejours, du, au, exclus) {
  // Même exclusion que le remplissage : un logement sans source vivante ne
  // dit rien de ses arrivées.
  const sortis = exclus instanceof Set ? exclus : new Set(exclus || []);
  return (sejours || []).filter(
    (s) =>
      !estAnnule(s) &&
      !estFerme(s) &&
      s.arrivee >= du &&
      s.arrivee < au &&
      !(s.logement && sortis.has(s.logement.id)),
  );
}

/** Filtre d'affichage (plateforme, texte, logement, statut, annulées). */
export function filtrer(sejours, f) {
  const q = String(f.texte || "").trim().toLowerCase();
  return (sejours || []).filter((s) => {
    const pf = plateformeDe(s.source, s.type);
    if (estAnnule(s) && !f.annulees) return false;
    if (f.plateforme && f.plateforme !== "toutes" && pf !== f.plateforme) return false;
    if (f.logement && (!s.logement || s.logement.id !== f.logement)) return false;
    if (f.statut === "confirmees" && (estAnnule(s) || estFerme(s))) return false;
    if (f.statut === "bloque" && !estFerme(s)) return false;
    if (f.statut === "annulees" && !estAnnule(s)) return false;
    if (q) {
      const tx = `${s.voyageur || ""} ${(s.logement && s.logement.nom) || ""}`.toLowerCase();
      if (!tx.includes(q)) return false;
    }
    return true;
  });
}

/**
 * La place d'une barre dans la grille du mois : la barre commence au milieu
 * du jour d'arrivée et finit au milieu du jour de départ (comme la maquette).
 * Rend { gauche, largeur } en nombre de colonnes, ou null hors de la période.
 */
export function placeBarre(s, jours) {
  if (!jours.length) return null;
  const premier = jours[0];
  const dernier = jours[jours.length - 1];
  if (s.depart <= premier || s.arrivee > dernier) return null;
  const iA = s.arrivee < premier ? -0.5 : jours.indexOf(s.arrivee);
  const iD = s.depart > dernier ? jours.length - 0.5 + 0.5 : jours.indexOf(s.depart);
  if (iA === -1 || iD === -1) return null;
  const gauche = Math.max(0, iA + 0.5);
  const droite = Math.min(jours.length, iD + 0.5);
  if (droite <= gauche) return null;
  return { gauche, largeur: droite - gauche, coupeAvant: s.arrivee < premier, coupeApres: s.depart > dernier };
}

/**
 * La vue « Jour » : rotations (un départ et une arrivée le même jour sur le
 * même logement), départs, arrivées, sur place ce soir.
 */
export function vueJour(sejours, jour) {
  const actifs = (sejours || []).filter((s) => !estAnnule(s) && !estFerme(s));
  const departs = actifs.filter((s) => s.depart === jour);
  const arrivees = actifs.filter((s) => s.arrivee === jour);
  const rotations = [];
  const restantsD = [];
  for (const d of departs) {
    const a = arrivees.find((x) => x.logement && d.logement && x.logement.id === d.logement.id);
    if (a) rotations.push({ depart: d, arrivee: a });
    else restantsD.push(d);
  }
  const enRotation = new Set(rotations.map((r) => r.arrivee));
  const restantsA = arrivees.filter((a) => !enRotation.has(a));
  const surPlace = actifs.filter((s) => s.arrivee <= jour && s.depart > jour);
  return { rotations, departs: restantsD, arrivees: restantsA, surPlace, total: rotations.length + restantsD.length + restantsA.length };
}

export function nuits(s) {
  return joursEntre(s.arrivee, s.depart).length;
}

/** La ligne qui dit quels logements sortent du remplissage (ou null). */
export function phraseExclusCalendrier(nonCouverts, logements) {
  const ids = new Set((logements || []).map((l) => l.id));
  const dedans = (nonCouverts || []).filter((l) => ids.has(l.id));
  if (!dedans.length) return null;
  const tous = dedans.length >= ids.size;
  const liste = dedans.map((l) => `${l.nom || l.id} (${l.motif})`).join(", ");
  return tous
    ? `— Aucun logement n'a de source vivante de réservations (${liste}) : remplissage, nuits libres et arrivées de la semaine ne sont pas calculés.`
    : `— Remplissage, nuits libres et arrivées de la semaine calculés sans ${pl(dedans.length, "logement")} sans source vivante : ${liste}.`;
}
